import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { FeatureFlagService } from './FeatureFlagService';
import { constants } from '@/const';
import { UserService } from './UserService';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;
type TourDefinition = () => ShepherdTour;
type TourTrackingContext = {
  userId: number;
  tourId: number;
};
type TrackableTour = ShepherdTour & {
  __tourStartDate?: string | null;
};

const tourRegistry: Map<string, { definition: TourDefinition; featureFlag?: string }> = new Map();

export class TourService {

  public static registerTour(name: string, definition: TourDefinition, featureFlag?: string) {
    tourRegistry.set(name, { definition, featureFlag });
  }

  public static get tourActive(): boolean {
    return document.querySelectorAll('.shepherd-element').length > 0;
  }

  public static startTour(name: string, trackingContext?: TourTrackingContext) {
    const entry = tourRegistry.get(name);
    if (!entry) {
      return;
    }

    if (entry.featureFlag && !FeatureFlagService.getFeatureEnabled(entry.featureFlag)) {
      return;
    }

    const tour = entry.definition();
    attachTourPersistenceHandlers(tour, trackingContext);
    tour.start();
  }

  public static startEligibleTours(tourNames: string[], trackingContext?: TourTrackingContext) {
    for (const name of tourNames) {
      this.startTour(name, trackingContext);
    }
  }
}

function isValidTourTrackingContext(context?: TourTrackingContext): context is TourTrackingContext {
  return !!context
    && Number.isInteger(context.userId)
    && context.userId > 0
    && Number.isInteger(context.tourId)
    && context.tourId > 0;
}

function attachTourPersistenceHandlers(tour: ShepherdTour, context?: TourTrackingContext): void {
  if (!isValidTourTrackingContext(context)) {
    return;
  }

  let endDate: string | null = null;
  let hasPersisted = false;
  const persistTourRecord = () => {
    if (hasPersisted) {
      return;
    }

    hasPersisted = true;
    const startDate = ((tour as TrackableTour).__tourStartDate ?? null);
    UserService.UpsertUserTours(context.userId, context.tourId, startDate, endDate)
      .catch(() => {
        // Keep tour UX uninterrupted if persistence fails.
      });
  };

  tour.on('complete', () => {
    endDate = new Date().toISOString();
    persistTourRecord();
  });

  tour.on('cancel', () => {
    const startDate = ((tour as TrackableTour).__tourStartDate ?? null);
    if (startDate === null) {
      endDate = new Date().toISOString();
    }
    persistTourRecord();
  });
}

function markTourStartDate(tour: ShepherdTour): void {
  const trackableTour = tour as TrackableTour;
  if (trackableTour.__tourStartDate === null || typeof trackableTour.__tourStartDate === 'undefined') {
    trackableTour.__tourStartDate = new Date().toISOString();
  }
}

// --- Helpers ---

function setButtonLoading(tour: ShepherdTour, loading: boolean): void {
  const currentStep = tour.getCurrentStep();
  if (!currentStep) return;
  const btn = currentStep.getElement()?.querySelector('.shepherd-footer .shepherd-button') as HTMLButtonElement;
  if (!btn) return;

  if (loading) {
    btn.disabled = true;
    btn.dataset.originalText = btn.innerHTML;
    btn.innerHTML = '<span class="tour-spinner"></span> Loading...';
  } else {
    btn.disabled = false;
    if (btn.dataset.originalText) {
      btn.innerHTML = btn.dataset.originalText;
    }
  }
}

function injectStepUI(stepEl: Element | null | undefined, stepNumber: number, totalSteps: number): void {
  if (!stepEl) return;

  // Inject step counter into footer
  const footer = stepEl.querySelector('.shepherd-footer');
  if (footer) {
    const existingCounter = footer.querySelector('.tour-step-counter-footer');
    if (existingCounter) existingCounter.remove();

    const counter = document.createElement('div');
    counter.className = 'tour-step-counter-footer';
    counter.textContent = `${stepNumber} of ${totalSteps}`;
    footer.insertBefore(counter, footer.firstChild);
  }

  // Inject feature tag into header
  const header = stepEl.querySelector('.shepherd-header');
  if (header) {
    const existingTag = header.querySelector('.tour-feature-tag');
    if (existingTag) existingTag.remove();

    const tag = document.createElement('div');
    tag.className = 'tour-feature-tag';
    tag.textContent = 'FEATURE HIGHLIGHT';
    header.insertBefore(tag, header.firstChild);
  }
}

function pollUntil(condition: () => boolean, intervalMs = 100, timeoutMs = 10000): Promise<void> {
  return new Promise<void>((resolve) => {
    const startedAt = Date.now();
    const poll = () => {
      if (condition() || Date.now() - startedAt > timeoutMs) {
        resolve();
        return;
      }
      setTimeout(poll, intervalMs);
    };
    poll();
  });
}

function waitForElement(selector: string, timeoutMs = 10000): Promise<void> {
  return pollUntil(() => !!document.querySelector(selector), 100, timeoutMs);
}

function waitForEnabledSelect(labelSelector: string, timeoutMs = 10000): Promise<void> {
  return pollUntil(() => {
    const label = document.querySelector(labelSelector);
    const vSelect = label?.closest('.selection-step')?.querySelector('.v-select');
    return !!label && !vSelect?.classList.contains('vs--disabled');
  }, 200, timeoutMs);
}

// --- Vue Instance Access ---

function getVueInstance(el: Element | null): any {
  return el ? (el as any).__vue__ : null;
}

function getVSelectInContainer(containerSelector: string): any {
  const container = document.querySelector(containerSelector);
  if (!container) return null;
  const vSelectEl = container.classList?.contains('v-select')
    ? container
    : container.querySelector('.v-select');
  return getVueInstance(vSelectEl);
}

function getVSelectByLabelFor(forValue: string): any {
  const label = document.querySelector(`label[for="${forValue}"]`);
  const vSelectEl = label?.closest('.selection-step')?.querySelector('.v-select') ?? null;
  return getVueInstance(vSelectEl);
}

function selectVSelectOption(instance: any, labelProp: string, value: string): boolean {
  if (!instance?.options?.length) return false;

  const upperValue = value.toUpperCase();
  const option = instance.options.find((o: any) => {
    const optionValue: string = labelProp ? o[labelProp] : (typeof o === 'string' ? o : '');
    return optionValue?.toUpperCase().includes(upperValue);
  });

  if (!option) return false;

  instance.select(option);
  instance.open = false;
  instance.$refs?.search?.blur();
  return true;
}

function autoSelectInContainer(containerSelector: string, labelProp: string, value: string, timeoutMs = 10000): Promise<void> {
  return pollUntil(() => {
    const instance = getVSelectInContainer(containerSelector);
    return selectVSelectOption(instance, labelProp, value);
  }, 200, timeoutMs);
}

function autoSelectByLabel(forValue: string, labelProp: string, value: string, timeoutMs = 10000): Promise<void> {
  return pollUntil(() => {
    const instance = getVSelectByLabelFor(forValue);
    return selectVSelectOption(instance, labelProp, value);
  }, 200, timeoutMs);
}

// --- Tree Navigation ---

function getTruckSelectorInstance(): any {
  const el = document.querySelector('.truck-selection-embedded') || document.querySelector('.truck-selection-page');
  return getVueInstance(el);
}

function collectBranchIds(nodes: any[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (node.hasChildren) {
      ids.push(node.id);
      if (node.children?.length) {
        ids.push(...collectBranchIds(node.children));
      }
    }
  }
  return ids;
}

function expandAllTreeNodes(): void {
  const instance = getTruckSelectorInstance();
  if (!instance?.displayedComponents) return;

  const allIds = collectBranchIds(instance.displayedComponents);
  for (const id of allIds) {
    if (!instance.expandedComponentIds.includes(id)) {
      instance.expandedComponentIds.push(id);
    }
  }
}

function findLeafByLabel(nodes: any[], label: string): any {
  const upperLabel = label.toUpperCase();
  for (const node of nodes) {
    const nodeLabel = (node.componentDescription || node.name || '').toUpperCase();
    if (!node.hasChildren && nodeLabel.includes(upperLabel)) {
      return node;
    }
    if (node.children?.length) {
      const found = findLeafByLabel(node.children, label);
      if (found) return found;
    }
  }
  return null;
}

function expandTreeAndSelectLeaf(leafLabel: string): void {
  const instance = getTruckSelectorInstance();
  if (!instance?.displayedComponents) return;

  expandAllTreeNodes();

  const leaf = findLeafByLabel(instance.displayedComponents, leafLabel);
  if (leaf) {
    instance.ToggleComponent(leaf);
  }
}

function findLeafElement(leafLabel: string): HTMLElement | null {
  const rows = document.querySelectorAll<HTMLElement>('.tree-node__row.tree-node__row--leaf');
  const upperLabel = leafLabel.toUpperCase();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.textContent?.toUpperCase().includes(upperLabel)) {
      return row;
    }
  }
  return null;
}

function selectLeafComponent(leafLabel: string): void {
  const instance = getTruckSelectorInstance();
  if (!instance?.displayedComponents) return;

  const leaf = findLeafByLabel(instance.displayedComponents, leafLabel);
  if (leaf) {
    instance.ToggleComponent(leaf);
  }
}

function createOEMSearchTour(): ShepherdTour {
  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      classes: 'shepherd-theme-custom',
      scrollTo: true,
      cancelIcon: {
        enabled: true
      },
      modalOverlayOpeningPadding: 6,
      modalOverlayOpeningRadius: 6
    }
  });
  (tour as TrackableTour).__tourStartDate = null;

  // Step 1: Click the Parts Lookup button
  tour.addStep({
    id: "step1",
    text: '<div class="tour-step-counter">1 of 7</div><strong class="tour-step-header">Did you know? We have an enhanced <em>Parts Lookup</em> feature.</strong><br>This allows you to search for both OEM and competitive truck models and identify compatible parts.',
    attachTo: {
      element: '#tmsButtonGroup a[href*="/tms/partsLookup"]',
      on: "bottom"
    },
    canClickTarget: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 1, 7);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          markTourStartDate(tour);
          setButtonLoading(tour, true);
          // Only navigate if we're not already on the Parts Lookup page —
          // clicking the link again would needlessly re-navigate/reload it.
          if (!document.querySelector('#psts__make')) {
            const link = document.querySelector('#tmsButtonGroup a[href*="/tms/partsLookup"]') as HTMLElement;
            if (link) {
              link.click();
            }
          }
          await waitForElement('#psts__make', 15000);
          tour.next();
        }
      }
    ]
  });

  // Step 2: Select the truck Make → auto-selects "CAT LIFT TRUCKS"
  tour.addStep({
    id: "step2",
    text: '<div class="tour-step-counter">2 of 7</div><strong class="tour-step-header">Select the truck Make</strong><br>Type or choose the manufacturer to locate the correct OEM truck.',
    attachTo: {
      element: '#psts__make',
      on: "right"
    },
    canClickTarget: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 2, 7);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          setButtonLoading(tour, true);
          await autoSelectInContainer('#psts__make', 'OemDescription', 'CAT LIFT TRUCKS');
          await waitForEnabledSelect('label[for="modelSelect"]', 15000);
          tour.next();
        }
      }
    ]
  });

  // Step 3: Select the Model → auto-selects "EC25N"
  tour.addStep({
    id: "step3",
    text: '<div class="tour-step-counter">3 of 7</div><strong class="tour-step-header">Select the Model</strong><br>Enter or pick your specific model.',
    attachTo: {
      element: '#psts__model',
      on: "right"
    },
    canClickTarget: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 3, 7);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          setButtonLoading(tour, true);
          await autoSelectByLabel('modelSelect', 'ModelName', 'EC25N');
          await waitForEnabledSelect('label[for="componentGroupSelect"]', 15000);
          tour.next();
        }
      }
    ]
  });

  // Step 4: Choose a Publication → auto-selects "CHASSIS, MAST & OPTIONS"
  tour.addStep({
    id: "step4",
    text: '<div class="tour-step-counter">4 of 7</div><strong class="tour-step-header">Choose a Publication</strong><br>Select the relevant publication to access detailed parts information.',
    attachTo: {
      element: '#psts__publication',
      on: "right"
    },
    canClickTarget: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 4, 7);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          setButtonLoading(tour, true);
          await autoSelectByLabel('componentGroupSelect', 'PublicationModuleTitle', 'CHASSIS, MAST & OPTIONS');
          await waitForElement('.tree-node__label', 20000);
          tour.next();
        }
      }
    ]
  });

  // Step 5: Navigate to the Component → expands tree and scrolls to "Hydraulic Pump"
  tour.addStep({
    id: "step5",
    text: '<div class="tour-step-counter">5 of 7</div><strong class="tour-step-header">Navigate to the Component</strong><br>Browse through the component hierarchy until you find the section you need.',
    attachTo: {
      element: '.tree-node--root',
      on: "right"
    },
    canClickTarget: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 5, 7);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          setButtonLoading(tour, true);
          expandAllTreeNodes();
          await waitForElement('.tree-node__label', 20000);
          tour.next();
        }
      }
    ]
  });

  // Step 6: Select the Component → triggers parts list load
  tour.addStep({
    id: "step6",
    text: '<div class="tour-step-counter">6 of 7</div><strong class="tour-step-header">Select the Component</strong><br>Click on the component to load its associated parts list.',
    attachTo: {
      element: () => findLeafElement('01-71-01 HYDRAULIC PUMPS'),
      on: "right"
    },
    canClickTarget: false,
    scrollTo: { behavior: 'smooth', block: 'center' },
    when: {
      show() {
        injectStepUI(this.getElement(), 6, 7);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          setButtonLoading(tour, true);
          selectLeafComponent('01-71-01 HYDRAULIC PUMPS');
          await waitForElement('.diagram-results-panel__pagination-row', 20000);
          tour.next();
        }
      }
    ]
  });

  // Step 7: View Parts List
  tour.addStep({
    id: "step7",
    text: '<div class="tour-step-counter">7 of 7</div><strong class="tour-step-header">View Parts List</strong><br>Once you\'ve selected a component, the system will load the parts list. Choose the desired part(s) and add them to your cart for quick and easy checkout.',
    attachTo: {
      element: '.diagram-results-panel',
      on: "left-start"
    },
    canClickTarget: false,
    scrollTo: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 7, 7);
      }
    },
    buttons: [
      {
        text: "Close",
        action: tour.complete
      }
    ]
  });

  return tour;
}

function createOEMDiagramsTour(): ShepherdTour {
  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      classes: 'shepherd-theme-custom',
      scrollTo: true,
      cancelIcon: {
        enabled: true
      },
      modalOverlayOpeningPadding: 6,
      modalOverlayOpeningRadius: 6
    }
  });
  (tour as TrackableTour).__tourStartDate = null;

  // Step 1: View OEM Diagrams
  tour.addStep({
    id: "step1",
    text: '<strong class="tour-step-header">New OEM Diagrams Available</strong><br>OEM diagrams are now available on DealerNet along with the corresponding OEM parts list. You can navigate around the diagram by dragging and zooming. Selecting a numbered part in the diagram will highlight the corresponding part in the list.',
    attachTo: {
      element: '.svg-diagram__content',
      on: "left"
    },
    canClickTarget: false,
    scrollTo: false,
    when: {
      show() {
        markTourStartDate(tour);
      }
    },
    buttons: [
      {
        text: "Close",
        action: tour.complete
      }
    ]
  });

  return tour;
}

// Register tours
TourService.registerTour('OEM Search Tour', createOEMSearchTour, constants.FEATURE_NAMES.TOUR_OEM_SEARCH);
TourService.registerTour('OEM Diagrams Tour', createOEMDiagramsTour, constants.FEATURE_NAMES.TOUR_OEM_DIAGRAMS);
